import { css } from 'styled-components';
import { media, pxToRem, vars } from '$util/theme';

const { fontSizeBase } = vars.quantumverse.reskinTypography;
const { purple500, purple800, neutral600, neutral700, neutral800 } = vars.quantumverse.reskinColors;

const makeTypographyCss = (
  fontSize: number | string,
  lineHeight: number,
  fontWeight: number = vars.fontWeight.regular,
  color: string = neutral600,
  letterSpacing?: string
) => {
  const fontSizeNo = typeof fontSize === 'number' ? fontSize : parseInt(fontSize, 10);
  return css`
    font-size: ${pxToRem(fontSizeNo, fontSizeBase)};
    line-height: ${lineHeight / fontSizeNo};
    font-weight: ${fontWeight};
    color: ${color};
    ${letterSpacing && `letter-spacing: ${letterSpacing};`}

    // handle text breaking
    // @QUESTION why did I do this?!...????
    overflow-wrap: break-word;
    word-break: break-word;
  `;
};

/**
 * Because components are standalone we'll add the text typography
 * per component instead of per page layout.
 *
 * @see https://www.figma.com/file/GJA4hfZndIKiAsmxgtncFo/Contentful-Design-System?node-id=11%3A65
 */

export const textStandardCss = makeTypographyCss(16, 24, 400, neutral600);
export const desktopTitleH1Css = makeTypographyCss(56, 56, 600, purple800, '-1.68px');
/**
 * @deprecated use desktop version
 */
export const mobileTitleH1Css = makeTypographyCss(36, 36, 600, purple800);
export const desktopTitleH2Css = makeTypographyCss(40, 44, 600, purple800, '-1.4px');
/**
 * @deprecated use desktop version
 */
export const mobileTitleH2Css = makeTypographyCss(32, 36, 600, neutral800);
export const desktopTitleH3Css = makeTypographyCss(32, 40, 600, purple800, '-1.12px');
/**
 * @deprecated use desktop version
 */
export const mobileTitleH3Css = makeTypographyCss(20, 24, 600, purple800);
export const desktopTitleH4Css = makeTypographyCss(28, 32, 600, purple800, '-0.84px');
/**
 * @deprecated use desktop version
 */
export const mobileTitleH4Css = makeTypographyCss(20, 24, 400, purple800);
export const desktopTitleH5Css = makeTypographyCss(24, 28, 600, neutral800, '-0.48px');
/**
 * @deprecated use desktop version
 */
export const mobileTitleH5Css = makeTypographyCss(16, 24, 600, neutral800);
export const desktopTitleH6Css = makeTypographyCss(22, 28, 600, neutral800, '-0.44px');
/**
 * @deprecated use desktop version
 */
export const mobileTitleH6Css = makeTypographyCss(22, 28, 500, neutral800);
export const mobileTitleH7Css = makeTypographyCss(32, 36, 600, neutral800);
export const desktopTitleH7Css = makeTypographyCss(40, 44, 600, purple800);

export const desktopSubtitleH1Css = makeTypographyCss(18, 22, 400, purple800);
export const mobileSubtitleH1Css = makeTypographyCss(18, 22, 400, neutral700);

export const desktopBodyQuoteCss = makeTypographyCss(21, 26, 600, neutral600);
export const mobileBodyQuoteCss = makeTypographyCss(24, 28, 600, neutral600);
export const desktopBodyLargeCss = makeTypographyCss(18, 28);
export const desktopBodyLargeListCss = makeTypographyCss(16, 24, 400, neutral600);
export const desktopBodyStandardCss = makeTypographyCss(16, 24, 400, neutral700);
export const mobileBodyStandardCss = makeTypographyCss(16, 24, 400, neutral600);
export const desktopBodyMinusculeCss = makeTypographyCss(14, 20, 600, neutral600);
export const mobileBodyStandardListCss = makeTypographyCss(16, 24, 400, neutral700);

export const desktopLabelLargeCss = makeTypographyCss(20, 32);
export const mobileLabelLargeCss = makeTypographyCss(20, 24);
export const desktopLabelStandartCss = makeTypographyCss(16, 24);
export const mobileLabelStandartCss = makeTypographyCss(18, 24);
export const desktopLabelAllCapsCss = makeTypographyCss(14, 20, 600);

export const desktopButtonExtraLargeCss = makeTypographyCss(20, 24, 600, purple500);
export const mobileButtonExtraLargeCss = makeTypographyCss(20, 24, 600, purple500);
export const desktopButtonLargeCss = makeTypographyCss(18, 24, 700, purple500);
export const desktopButtonStandartCss = makeTypographyCss(16, 28, 700, purple500);
export const mobileButtonStandartCss = makeTypographyCss(16, 28, 700, purple500);

export const linkStandartCss = css`
  ${desktopBodyStandardCss};
  text-decoration: underline !important;
  :visited,
  :link {
    color: ${vars.quantumverse.reskinColors.neutral800};
  }
  :hover {
    color: ${vars.quantumverse.reskinColors.purple500};
  }
  :active {
    color: ${vars.quantumverse.reskinColors.purple600};
  }

  font-weight: 600;
`;

export const listBaseCss = css`
  ul > li {
    margin-bottom: 10px;
    font-family: 'Rubik';
    > p {
      margin: 0;
    }
  }
`;

export const listLargeCss = css`
  ${listBaseCss}
  ul > li,
  ul > li p {
    ${media.up('md')} {
      ${desktopBodyLargeListCss}
    }
    ${media.down('md')} {
      ${mobileBodyStandardListCss}
    }
  }
`;
